import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { InvokeManualResize } from '@memberjunction/global';
import { filter } from 'rxjs';
import { MetadataLoadingStage, UserContextService } from 'src/app/core/services/user-context.service';
import { ORGANIZATION_ROLE_ROUTE_ACCESS, OrganizationRoleRouteAccess } from '../core/route-access.config';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css'],
})
export class SettingsComponent implements OnInit, AfterViewInit {
  selectedOption: string = 'profile';
  metadataLoadingStage: MetadataLoadingStage = MetadataLoadingStage.NotStarted;
  isUserAdmin: boolean = false;
  isUserOwner: boolean = false;
  isUserInternalSuperAdmin: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router, 
    private userContextService: UserContextService
  ) {
    this.userContextService.MetadataLoadingStage$.subscribe((stage) => {
      this.metadataLoadingStage = stage;
    });
  }

  public routeAccess: OrganizationRoleRouteAccess[] = ORGANIZATION_ROLE_ROUTE_ACCESS;

  ngOnInit(): void {
    // Determine user roles
    switch (this.userContextService.OrganizationContact.ContactRole.trim().toLowerCase()) {
      case 'owner':
        this.isUserOwner = true;
        this.isUserAdmin = true;
        break;
      case 'admin':
        this.isUserAdmin = true;
        break;
    }
    this.isUserInternalSuperAdmin = this.userContextService.IsContactInternalSuperAdmin;

    // Update selected option whenever route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // The activeChild's path (e.g., 'profile', 'subscription-list', etc.)
        const activeChild = this.route.snapshot.firstChild;
        this.selectedOption = activeChild?.routeConfig?.path || 'profile';
    });
  }

  ngAfterViewInit(): void {
    InvokeManualResize(); // fire this off to ensure our component is resized properly
  }

  isActive(path: string): boolean {
    return this.router.url.startsWith(`/settings/${path}`);
  }

  selectOption(optionPath: string): void {
    this.router.navigate(['/settings', optionPath]);
  }

  showOption(option: OrganizationRoleRouteAccess): boolean {
    const path = option.path;
    const requiresOwner = option.requiresOwner;
    const requiresAdmin = option.requiresAdmin;
    const requiresInternalSuperAdmin = option.requiresInternalSuperAdmin;

    if (requiresInternalSuperAdmin && !this.isUserInternalSuperAdmin) {
      return false;
    }
    if (requiresOwner && !this.isUserOwner) {
      return false;
    }
    if (requiresAdmin && !this.isUserAdmin) {
      return false;
    }

    switch (path) {
      case 'profile':
        return true;
      case 'organizations':
        return this.metadataLoadingStage >= MetadataLoadingStage.OrganizationLoaded;
      case 'team':
        return this.metadataLoadingStage >= MetadataLoadingStage.OrganizationLoaded;
      case 'security':
        return this.metadataLoadingStage >= MetadataLoadingStage.OrganizationLoaded;
      case 'subscription-list':
        return this.metadataLoadingStage >= MetadataLoadingStage.SubscriptionsLoaded;
      case 'instance-list':
        return this.metadataLoadingStage >= MetadataLoadingStage.InstanceMetadataLoaded;
      case 'connectors':
        return this.metadataLoadingStage >= MetadataLoadingStage.InstanceConnectorsLoaded;
      case 'internal-admin':
        return this.metadataLoadingStage >= MetadataLoadingStage.ContactLoaded;
      default:
        return false;
    }
  }
}
